import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const STAT_COLS = [
    { key: "name", label: "Player", sticky: true },
    { key: "jersey_number", label: "#" },
    { key: "position", label: "Pos" },
    { key: "games_played", label: "GP" },
    { key: "ppg", label: "PPG" },
    { key: "rpg", label: "RPG" },
    { key: "apg", label: "APG" },
    { key: "spg", label: "SPG" },
    { key: "bpg", label: "BPG" },
    { key: "fg_pct", label: "FG%" },
    { key: "three_pct", label: "3P%" },
    { key: "ft_pct", label: "FT%" },
];

export default function Stats() {
    const [players, setPlayers] = useState([]);
    const [sortKey, setSortKey] = useState("ppg");
    const [sortDir, setSortDir] = useState("desc");

    useEffect(() => { api.get("/players").then((r) => setPlayers(r.data)); }, []);

    const sorted = useMemo(() => {
        const arr = [...players];
        arr.sort((a, b) => {
            const av = a[sortKey] ?? 0;
            const bv = b[sortKey] ?? 0;
            if (typeof av === "string") {
                return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
            }
            return sortDir === "asc" ? av - bv : bv - av;
        });
        return arr;
    }, [players, sortKey, sortDir]);

    const toggleSort = (key) => {
        if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
        else { setSortKey(key); setSortDir(key === "name" || key === "position" ? "asc" : "desc"); }
    };

    const leaders = useMemo(() => {
        if (!players.length) return [];
        const max = (k) => players.reduce((a, p) => (p[k] || 0) > (a[k] || 0) ? p : a, players[0]);
        return [
            { label: "Points", key: "ppg", player: max("ppg"), unit: "PPG" },
            { label: "Rebounds", key: "rpg", player: max("rpg"), unit: "RPG" },
            { label: "Assists", key: "apg", player: max("apg"), unit: "APG" },
            { label: "Steals", key: "spg", player: max("spg"), unit: "SPG" },
            { label: "Blocks", key: "bpg", player: max("bpg"), unit: "BPG" },
            { label: "3pt %", key: "three_pct", player: max("three_pct"), unit: "3P%" },
        ];
    }, [players]);

    return (
        <div data-testid="stats-page" className="grain">
            <section className="border-b border-line py-16 md:py-24 bg-bg-soft">
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase font-bold mb-4">By The Numbers</div>
                    <h1 className="h-display text-6xl md:text-8xl">Player Stats</h1>
                    <p className="text-ink text-lg mt-5 max-w-xl">Season averages — 2025-2026 ABA.</p>
                </div>
            </section>

            {/* Leaders */}
            <section className="max-w-7xl mx-auto px-5 md:px-10 py-12">
                <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-6">League Leaders</div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {leaders.map((l, i) => (
                        <div key={l.key} className="card-flat p-5" data-testid={`leader-${l.key}`}>
                            <div className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold">{l.label}</div>
                            <div className="h-display text-3xl text-gold mt-3 leading-none">
                                {(l.player?.[l.key] ?? 0).toFixed(1)}
                            </div>
                            <div className="text-[10px] uppercase text-ink tracking-widest mt-1">{l.unit}</div>
                            <div className="text-sm mt-3 text-white truncate">{l.player?.name}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Sortable table */}
            <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20">
                <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-6">Full Stats</div>
                <div className="card-flat overflow-x-auto" data-testid="stats-table">
                    <table className="w-full text-sm">
                        <thead className="bg-bg-soft border-b border-line">
                            <tr>
                                {STAT_COLS.map((c) => (
                                    <th
                                        key={c.key}
                                        onClick={() => toggleSort(c.key)}
                                        data-testid={`stat-h-${c.key}`}
                                        className={`p-3 text-[10px] uppercase tracking-widest text-ink font-bold cursor-pointer hover:text-gold select-none ${c.sticky ? "text-left sticky left-0 bg-bg-soft" : "text-right"}`}
                                    >
                                        <span className="inline-flex items-center gap-1">
                                            {c.label}
                                            {sortKey === c.key ? (sortDir === "desc" ? <ArrowDown size={10} /> : <ArrowUp size={10} />) : <ArrowUpDown size={10} className="opacity-30" />}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((p, i) => (
                                <tr key={p.id} className="border-t border-line hover:bg-bg-soft/40" data-testid={`stat-row-${i}`}>
                                    {STAT_COLS.map((c) => {
                                        const val = p[c.key];
                                        const display = c.key.endsWith("_pct")
                                            ? `${(val || 0).toFixed(1)}%`
                                            : ["ppg","rpg","apg","spg","bpg"].includes(c.key)
                                                ? (val || 0).toFixed(1)
                                                : val ?? "—";
                                        return (
                                            <td key={c.key} className={`p-3 ${c.sticky ? "text-left sticky left-0 bg-card" : "text-right h-display"}`}>
                                                {c.sticky ? (
                                                    <div className="flex items-center gap-3">
                                                        {p.photo_url && <img src={p.photo_url} alt="" className="w-8 h-8 object-cover" />}
                                                        <div className="text-white text-sm font-semibold">{p.name}</div>
                                                    </div>
                                                ) : (
                                                    <span className={sortKey === c.key ? "text-gold" : ""}>{display}</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            {sorted.length === 0 && (
                                <tr><td colSpan={STAT_COLS.length} className="p-12 text-center text-ink">No stats yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
